import moment from 'moment-timezone';
import BaseModel from 'volta/models/base-model';
import { collectionCommand, resourceCommand as command } from 'volta/utils/api';

import { attr, belongsTo } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';

import type {
  ICreateSimulationCmd,
  IDuplicateSimulationCmd,
  ISelectScenarioCmd,
  ISelectedScenario,
  IUpdateSimulationName,
  IValidateSimulationCmd,
  TScenarioStatus
} from './types/simulation';
import type User from './user';
import type { IAggTasksViewMap } from './types/tasks';

export const SimulationResourceName = {
  singular: 'simulation',
  plural: 'simulations'
};

export const SimulationCommands = {
  UpdateSimulationName: 'UpdateSimulationName',
  CreateSimulation: 'CreateSimulation',
  DuplicateSimulation: 'DuplicateSimulation',
  ValidateSimulation: 'ValidateSimulation',
  SelectScenario: 'SelectScenario'
};

const START_DATE = () => moment.utc().subtract(1, 'years').toDate();
const END_DATE = () => moment.utc().toDate();

export function getValidatedIntent(pct: number) {
  return pct >= 1 ? 'success' : pct >= 0.5 ? 'warning' : pct === 0 ? 'gray' : 'danger';
}

export default class Simulation extends BaseModel {
  static modelName = 'simulation';

  // Collection commands

  static createSimulation = collectionCommand<ICreateSimulationCmd, Simulation, typeof Simulation>(
    SimulationCommands.CreateSimulation
  );

  static duplicateSimulation = collectionCommand<
    IDuplicateSimulationCmd,
    Simulation,
    typeof Simulation
  >(SimulationCommands.DuplicateSimulation);

  // Commands

  updateSimulationName = command<IUpdateSimulationName, Simulation>(
    SimulationCommands.UpdateSimulationName
  );

  validateSimulation = command<IValidateSimulationCmd, Simulation>(
    SimulationCommands.ValidateSimulation
  );

  selectScenario = command<ISelectScenarioCmd, Simulation>(SimulationCommands.SelectScenario);

  // Attributes

  @attr('string') name!: string;
  @attr('string') description?: string;
  @attr('string', { defaultValue: 'READY' }) status?: TScenarioStatus;
  @attr('string') userId!: string;
  @attr('date', { defaultValue: START_DATE }) startDate!: Date;
  @attr('date', { defaultValue: END_DATE }) endDate!: Date;
  @attr('number', { defaultValue: 0 }) skuCount!: number;
  @attr('string') selectedScenarioId?: string;
  @attr('date') scenarioSelectedAt?: Date;
  @attr('string') scenarioSelectedBy?: string;
  @attr('date') validatedAt?: Date;
  @attr('string') validatedBy?: string;

  /**
   * User ids with whom the simulation is shared
   */
  @attr('array') sharedWith?: string[];

  /**
   * Simulation tasks info by type
   */
  @attr() tasks!: IAggTasksViewMap;

  /**
   * Simulation scenarios count including the reference scenario
   */
  @attr('array') scenarioIds!: string[];

  /**
   * Simulation reference scenario id
   */
  @attr('string') refScenarioId!: string;

  /**
   * Selected scenario info
   */
  @attr() selectedScenario?: ISelectedScenario;

  /**
   * Relationships
   */

  @belongsTo('user', { async: false, inverse: null })
  user!: User;

  get authorizedUsers() {
    return (this.sharedWith ?? []).concat(this.userId);
  }

  get validatedSettingsIntent() {
    const pct = this.selectedScenario?.settingsValidated;
    return getValidatedIntent(pct ?? 0);
  }

  get validatedForecastIntent() {
    const pct = this.selectedScenario?.forecastValidated;
    return getValidatedIntent(pct ?? 0);
  }

  hasScenario(scenarioId: string) {
    return this.scenarioIds.includes(scenarioId) || this.refScenarioId === scenarioId;
  }

  [Type] = 'simulation' as const;
}
